import { IconButton, LinkButton } from '@ads/components-react';
import { DocumentTextIcon, SparklesIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import type { TutorDocument } from '../../../types/tutorAi';
import { canImport, DOCUMENT_STATUS } from '../../../utils/documentStatus';
import { formatCount, formatDateTime, formatFileSize } from '../../../utils/format';
import { FileStatusTag } from '../../atoms/FileStatusTag';

export interface ContextFileRowProps {
  readonly document: TutorDocument;
  readonly onImport: (documentId: number) => void;
  readonly onDelete: (document: TutorDocument) => void;
}

/**
 * Linha de um documento de contexto: identificação, metadados, situação e ações.
 *
 * As ações são derivadas do status, não acumuladas: importar só aparece onde resolve algo —
 * pendente (nunca indexado, e é onde uma indexação que falhou deixa o documento) ou fora de
 * sincronia (índice não corresponde ao arquivo). Documento em dia não oferece ação além de
 * excluir, que está sempre disponível.
 *
 * Exibe `originalFileName` — `fileName` é o nome gerado no storage e não diz nada ao
 * operador.
 */
export function ContextFileRow({ document, onImport, onDelete }: ContextFileRowProps) {
  const { t } = useTranslation('tutorAI');

  const importLabel =
    document.status === DOCUMENT_STATUS.outOfSync
      ? t('classDetail.documents.actions.reimport')
      : t('classDetail.documents.actions.import');

  return (
    <li className="flex items-start gap-mini border-b-hairline border-ctx-layout-border px-xxs py-mini last:border-b-none">
      <DocumentTextIcon className="mt-quark h-xxxs w-xxxs shrink-0 text-ctx-content-placeholder" />

      <div className="flex min-w-none flex-1 flex-col gap-quark">
        <span className="truncate text-ctx-content-title ts-heading-xxs">
          {document.originalFileName}
        </span>
        <span className="text-ctx-content-placeholder ts-paragraph-xxs">
          {formatFileSize(document.sizeBytes)} ·{' '}
          {t('classDetail.documents.uploadedBy', {
            name: document.personName,
            date: formatDateTime(document.createdAt),
          })}
        </span>
        {document.chunkCount > 0 ? (
          <span className="text-ctx-content-placeholder ts-caption-xxs">
            {t('classDetail.documents.chunks', { total: formatCount(document.chunkCount) })}
          </span>
        ) : null}
      </div>

      <div className="flex shrink-0 items-center gap-xxs">
        <FileStatusTag status={document.status} />

        {canImport(document.status) ? (
          <LinkButton hierarchy="secondary" size="sm" onClick={() => onImport(document.id)}>
            <span className="flex items-center gap-quark">
              <SparklesIcon className="h-mini w-mini" />
              {importLabel}
            </span>
          </LinkButton>
        ) : null}

        <IconButton
          icon={TrashIcon}
          ariaLabel={t('classDetail.documents.actions.delete')}
          hierarchy="tertiary"
          size="md"
          onClick={() => onDelete(document)}
        />
      </div>
    </li>
  );
}
